'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { X, Menu, ChevronDown, Facebook, Linkedin, MapPin, Award } from 'lucide-react';

export default function MobileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setAboutOpen(false);
    toggleButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeMenu]);

  return (
    <>
      {/* Hamburger Button */}
      <button
        ref={toggleButtonRef}
        onClick={toggleMenu}
        className="lg:hidden text-white p-2 hover:bg-white/10 rounded transition"
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
        aria-controls="mobile-menu"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden ${isOpen ? '' : 'hidden'}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/* Mobile Menu Panel. Always rendered (not conditionally mounted) so its links — including
          the About submenu's /the-answerone-way, /locations, /about — exist in the static-exported
          HTML regardless of open state; visibility is CSS-driven via `hidden`/aria-hidden instead. */}
      <nav
        id="mobile-menu"
        className={`fixed top-0 right-0 h-full w-64 bg-brand-red text-white z-50 lg:hidden animate-slide-in ${isOpen ? '' : 'hidden'}`}
        aria-label="Mobile navigation"
        aria-hidden={!isOpen}
      >
          <div className="flex justify-end p-4">
            <button
              onClick={closeMenu}
              className="p-2 hover:bg-white/10 rounded transition"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex flex-col px-4 space-y-4">
            <Link
              href="/capabilities"
              className="py-3 px-4 hover:bg-white/10 rounded transition"
              onClick={closeMenu}
            >
              Capabilities
            </Link>
            <Link
              href="/services"
              className="py-3 px-4 hover:bg-white/10 rounded transition"
              onClick={closeMenu}
            >
              Plans
            </Link>
            <Link
              href="/industry"
              className="py-3 px-4 hover:bg-white/10 rounded transition"
              onClick={closeMenu}
            >
              Industries
            </Link>
            
            {/* About dropdown */}
            <div>
              <button
                onClick={() => setAboutOpen(!aboutOpen)}
                className="w-full text-left py-3 px-4 hover:bg-white/10 rounded transition flex items-center justify-between"
              >
                About
                <ChevronDown size={18} className={`transition-transform ${aboutOpen ? 'rotate-180' : ''}`} />
              </button>
              <div className={`pl-4 space-y-2 ${aboutOpen ? '' : 'hidden'}`}>
                <Link
                  href="/the-answerone-way"
                  className="block py-2 px-4 hover:bg-white/10 rounded transition"
                  onClick={closeMenu}
                >
                  Why AnswerOne
                </Link>
                <Link
                  href="/locations"
                  className="block py-2 px-4 hover:bg-white/10 rounded transition"
                  onClick={closeMenu}
                >
                  Areas We Serve
                </Link>
                <Link
                  href="/about"
                  className="block py-2 px-4 hover:bg-white/10 rounded transition"
                  onClick={closeMenu}
                >
                  About AnswerOne
                </Link>
              </div>
            </div>
            
            <Link
              href="/contact"
              className="py-3 px-4 hover:bg-white/10 rounded transition"
              onClick={closeMenu}
            >
              Contact
            </Link>

            <a
              href="tel:17187488888"
              className="bg-brand-black text-white px-6 py-3 font-bold hover:bg-opacity-90 transition text-center uppercase mt-4"
              onClick={closeMenu}
            >
              CALL (718) 748-8888
            </a>

            <div className="flex justify-center gap-6 pt-4 border-t border-white/10">
              <a href="https://www.facebook.com/AnswerOne.NY" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition" aria-label="Facebook">
                <Facebook size={24} />
              </a>
              <a href="https://www.linkedin.com/company/answerone" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition" aria-label="LinkedIn">
                <Linkedin size={24} />
              </a>
              <a href="https://www.google.com/maps/search/?api=1&query=AnswerOne+Inc%2C+7014+13th+Avenue%2C+Brooklyn%2C+NY+11228" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition" aria-label="Google Business Profile">
                <MapPin size={24} />
              </a>
              <a href="https://www.bbb.org/us/ny/brooklyn/profile/answering-service/answer-one-0121-2371" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition" aria-label="BBB Profile">
                <Award size={24} />
              </a>
            </div>
          </div>
      </nav>
    </>
  );
}
